import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";
import Cookies from "js-cookie";
import {
  FaAngleDoubleLeft, FaLock, FaSearch, FaUsersCog, FaShieldAlt, FaUsers
} from "react-icons/fa";
import { NAV_GROUPS, ADMIN_NAV_GROUPS, LOGO_SRC, LOGO_COLLAPSED_SRC, ADMIN_HIDDEN_PATHS } from "./navConfig";
import { useAccessConfig } from "./AccessContext";
import { useAdmin } from "./AdminContext";
import "./layout.css";

const LogoutModal = ({ isOpen, onClose, onConfirm, loader }) => {
  if (!isOpen) return null;
  return (
    <div className="logout-modal-overlay">
      <div className="logout-modal-card">
        <h3 className="modal-title">Confirm Logout</h3>
        <p className="modal-desc">
          Are you sure you want to end your session? You will be redirected to the login page.
        </p>
        <div className="modal-actions">
          <button className="modal-btn cancel" onClick={onClose} disabled={loader}>Cancel</button>
          <button className="modal-btn confirm" onClick={onConfirm} disabled={loader}>
            {loader ? "Logging Out..." : "Yes, Logout"}
          </button>
        </div>
      </div>
    </div>
  );
};

const LockedTooltip = ({ label, collapsed }) => (
  <div className={`locked-tooltip ${collapsed ? 'locked-tooltip--collapsed' : ''}`}>
    <div className="locked-tooltip__content">
      <FaLock className="locked-tooltip__icon" />
      <div className="locked-tooltip__text">
        <strong>Access Restricted</strong>
        <span>You don't have access to "{label}". Contact your admin to enable this feature.</span>
      </div>
    </div>
  </div>
);

const NavTooltip = ({ label, rect }) => {
  if (!rect) return null;
  const style = { top: `${rect.top + rect.height / 2}px`, left: `${rect.right}px` };
  return ReactDOM.createPortal(
    <div className="nav-tooltip" style={style}>
      <div className="nav-tooltip__content">{label}</div>
    </div>,
    document.body
  );
};

const Sidebar = ({ open, onClose, collapsed, toggleCollapsed, isDesktop }) => {
  const history = useHistory();
  const { accessConfig, loading: accessLoading } = useAccessConfig();
  const [logOutLoader, setLogOutLoader] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hoveredRect, setHoveredRect] = useState(null);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const pathname = history.location.pathname;

  const {
    isAdmin,
    clientsList,
    selectedClient,
    setSelectedClient,
    clearSelectedClient,
    viewMode,
    setViewMode,
  } = useAdmin();

  const scrollAreaRef = React.useRef(null);
  const searchInputRef = React.useRef(null);
  const searchBlurTimeoutRef = React.useRef(null);
  const isMounted = React.useRef(true);

  // Restore scroll position
  React.useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return;
    const savedPosition = sessionStorage.getItem('sidebarScrollPosition');
    if (savedPosition) scrollArea.scrollTop = parseInt(savedPosition, 10);
    const handleScroll = () => sessionStorage.setItem('sidebarScrollPosition', scrollArea.scrollTop.toString());
    scrollArea.addEventListener('scroll', handleScroll);
    return () => scrollArea.removeEventListener('scroll', handleScroll);
  }, []);

  React.useEffect(() => {
    return () => {
      isMounted.current = false;
      if (searchBlurTimeoutRef.current) clearTimeout(searchBlurTimeoutRef.current);
    };
  }, []);

  const confirmLogout = () => {
    setLogOutLoader(true);
    setTimeout(() => {
      localStorage.removeItem("credentials");
      localStorage.removeItem("versionData");
      sessionStorage.removeItem("adminSelectedClient");
      sessionStorage.removeItem("adminViewMode");
      Cookies.remove("idToken", { path: '/' });
      Cookies.remove("refreshToken", { path: '/' });
      Cookies.remove("accessToken", { path: '/' });
      history.replace("/login");
      if (isMounted.current) { setLogOutLoader(false); setShowLogoutConfirm(false); }
    }, 1000);
  };

  const handleNavClick = (path, item) => {
    if (!isAdmin && item.accessKey && !accessConfig[item.accessKey]) return;
    handleSearchCollapse();
    history.push(path);
    if (onClose) onClose();
  };

  const isItemLocked = (item) => {
    if (isAdmin) return false;
    if (!item.accessKey) return false;
    if (accessLoading) return false;
    return !accessConfig[item.accessKey];
  };

  React.useEffect(() => {
    if (searchExpanded) searchInputRef.current?.focus();
  }, [searchExpanded]);

  React.useEffect(() => {
    if (collapsed) {
      setSearchExpanded(false);
      setSearchQuery("");
      if (searchBlurTimeoutRef.current) { clearTimeout(searchBlurTimeoutRef.current); searchBlurTimeoutRef.current = null; }
    }
  }, [collapsed]);

  const searchableItems = React.useMemo(() => {
    const flat = [];
    NAV_GROUPS.forEach(g => {
      g.items.forEach(item => {
        // In admin mode, skip the 6 hidden tabs from search results too
        if (isAdmin && viewMode === 'admin' && ADMIN_HIDDEN_PATHS.has(item.path)) return;
        if (isAdmin || !item.accessKey || (accessLoading ? false : accessConfig[item.accessKey])) {
          flat.push({ ...item, groupLabel: g.label });
        }
      });
    });
    return flat;
  }, [accessConfig, accessLoading, isAdmin, viewMode]);

  const trimmedQuery = searchQuery.trim().toLowerCase();
  const filteredItems = React.useMemo(() => {
    if (!trimmedQuery) return [];
    return searchableItems.filter(item => item.label.toLowerCase().includes(trimmedQuery));
  }, [searchableItems, trimmedQuery]);

  const handleSearchExpand = () => { if (searchBlurTimeoutRef.current) { clearTimeout(searchBlurTimeoutRef.current); searchBlurTimeoutRef.current = null; } setSearchExpanded(true); };
  const handleSearchCollapse = () => { if (searchBlurTimeoutRef.current) { clearTimeout(searchBlurTimeoutRef.current); searchBlurTimeoutRef.current = null; } setSearchExpanded(false); setSearchQuery(""); };
  const handleSearchBlur = () => { searchBlurTimeoutRef.current = setTimeout(() => { searchBlurTimeoutRef.current = null; setSearchExpanded(false); setSearchQuery(""); }, 150); };
  const handleSearchFocus = () => { if (searchBlurTimeoutRef.current) { clearTimeout(searchBlurTimeoutRef.current); searchBlurTimeoutRef.current = null; } };
  const handleSearchKeyDown = (e) => { if (e.key === "Escape") handleSearchCollapse(); };

  // Helper to render a nav item button
  const renderNavItem = (item, opts = {}) => {
    const { locked = false, adminTool = false } = opts;
    const isActive = pathname === item.path;
    const isHovered = hoveredItem === item.path;
    return (
      <li key={item.path} className="sidebar-item-wrapper">
        <button
          type="button"
          className={[
            'sidebar-link',
            isActive ? 'sidebar-link--active' : '',
            locked ? 'sidebar-link--locked' : '',
            adminTool ? 'sidebar-link--admin-tool' : '',
            accessLoading && !adminTool ? 'sidebar-link--loading' : '',
          ].filter(Boolean).join(' ')}
          onClick={() => adminTool
            ? (handleSearchCollapse(), history.push(item.path), onClose && onClose())
            : handleNavClick(item.path, item)}
          onMouseEnter={(e) => {
            if (locked || collapsed) { setHoveredItem(item.path); setHoveredRect(e.currentTarget.getBoundingClientRect()); }
          }}
          onMouseLeave={() => { setHoveredItem(null); setHoveredRect(null); }}
          title={!collapsed && !locked ? item.label : ''}
          aria-disabled={locked || (!adminTool && accessLoading)}
          tabIndex={locked ? -1 : undefined}
          disabled={!adminTool && accessLoading}
        >
          <span className="sidebar-link__icon">{item.icon}</span>
          <span className="sidebar-link__text">{item.label}</span>
          {locked && <span className="sidebar-link__lock"><FaLock /></span>}
        </button>
        {locked && isHovered && <LockedTooltip label={item.label} collapsed={collapsed} />}
        {!locked && collapsed && isHovered && <NavTooltip label={item.label} rect={hoveredRect} />}
      </li>
    );
  };

  return (
    <>
      <LogoutModal
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={confirmLogout}
        loader={logOutLoader}
      />

      {/* Mobile Overlay */}
      <div
        className={`sidebar-overlay ${open ? "sidebar-overlay--open" : ""}`}
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (onClose) onClose(); } }}
        role="button" tabIndex={0} aria-label="Close menu"
      />

      {/* Sidebar Container */}
      <aside className={`app-sidebar ${open ? "app-sidebar--open" : ""} ${collapsed ? "app-sidebar--collapsed" : ""}`}>

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="sidebar-header">
          {searchExpanded ? (
            <div className="sidebar-search-wrap sidebar-search-wrap--full">
              <input
                ref={searchInputRef}
                type="text"
                className="sidebar-search-input sidebar-search-input--full"
                placeholder="Search tabs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onBlur={handleSearchBlur}
                onFocus={handleSearchFocus}
                onKeyDown={handleSearchKeyDown}
                aria-label="Search navigation"
              />
            </div>
          ) : (
            <>
              <button type="button" className="sidebar-logo-btn" onClick={() => handleNavClick("/", {})} aria-label="Go to home">
                <img alt="client-logo" className="sidebar-logo" src={collapsed ? LOGO_COLLAPSED_SRC : LOGO_SRC} />
              </button>
              {!collapsed && !(isAdmin && viewMode === 'client' && !selectedClient) && (
                <button type="button" className="sidebar-search-btn" onClick={handleSearchExpand} aria-label="Search navigation" title="Search tabs">
                  <FaSearch />
                </button>
              )}
            </>
          )}
          {!isDesktop && (
            <button className="sidebar-mobile-close" onClick={onClose} aria-label="Close menu">
              <i className="fas fa-times" />
            </button>
          )}
        </div>

        {/* ── Admin Mode Toggle ────────────────────────────────────────── */}
        {isAdmin && !collapsed && (
          <div className="admin-mode-toggle">
            <button
              className={`mode-toggle-btn ${viewMode === 'admin' ? 'mode-toggle-btn--active-admin' : ''}`}
              onClick={() => setViewMode('admin')}
              title="Work at admin level — no client required"
            >
              <FaShieldAlt className="mode-toggle-icon" />
              <span>Admin</span>
            </button>
            <button
              className={`mode-toggle-btn ${viewMode === 'client' ? 'mode-toggle-btn--active-client' : ''}`}
              onClick={() => setViewMode('client')}
              title="Configure a specific client"
            >
              <FaUsers className="mode-toggle-icon" />
              <span>Client</span>
            </button>
          </div>
        )}

        {/* Collapsed admin mode icon toggle */}
        {isAdmin && collapsed && (
          <div className="admin-mode-toggle-collapsed">
            <button
              className={`mode-toggle-icon-btn ${viewMode === 'admin' ? 'mode-toggle-icon-btn--active-admin' : ''}`}
              onClick={() => setViewMode('admin')}
              title="Admin Mode"
            >
              <FaShieldAlt />
            </button>
            <button
              className={`mode-toggle-icon-btn ${viewMode === 'client' ? 'mode-toggle-icon-btn--active-client' : ''}`}
              onClick={() => setViewMode('client')}
              title="Client Mode"
            >
              <FaUsers />
            </button>
          </div>
        )}

        {/* ── Client header (shows selected client info in client mode) ─ */}
        {isAdmin && !collapsed && viewMode === 'client' && selectedClient && (
          <div className="admin-active-client-header">
            <div className="admin-active-client-details">
              <span className="admin-active-label">Viewing Client</span>
              <span className="admin-active-name" title={selectedClient.center || selectedClient.org || selectedClient.userId}>
                {selectedClient.center || selectedClient.org || selectedClient.userId}
              </span>
              <span className="admin-active-client-id">{selectedClient.clientId}</span>
            </div>
            <button className="admin-change-client-btn" onClick={() => { clearSelectedClient(); history.push('/'); }}>
              Change
            </button>
          </div>
        )}

        {/* ── Scrollable Navigation Area ───────────────────────────────── */}
        <div className="sidebar-scroll-area" ref={scrollAreaRef}>

          {/* ── ADMIN MODE: all tabs unlocked, no client needed ─── */}
          {isAdmin && viewMode === 'admin' ? (
            <nav className="sidebar-nav">
              {trimmedQuery ? (
                filteredItems.length > 0 ? (
                  <div className="sidebar-group">
                    <span className="sidebar-group-label">Search results</span>
                    <ul className="sidebar-list">{filteredItems.map(item => renderNavItem(item, { adminTool: false }))}</ul>
                  </div>
                ) : (
                  <div className="sidebar-search-empty"><span>No matches for &quot;{searchQuery.trim()}&quot;</span></div>
                )
              ) : (
                NAV_GROUPS.map(group => {
                  const visibleItems = group.items.filter(
                    item => !ADMIN_HIDDEN_PATHS.has(item.path)
                  );
                  if (visibleItems.length === 0) return null;
                  return (
                    <div key={group.label} className="sidebar-group">
                      <span className="sidebar-group-label">{group.label}</span>
                      <ul className="sidebar-list">{visibleItems.map(item => renderNavItem(item, { adminTool: false }))}</ul>
                    </div>
                  );
                })
              )}
            </nav>

          ) : isAdmin && !selectedClient ? (
            /* ── CLIENT MODE, NO CLIENT: client picker list ─── */
            <div className="admin-client-list-mode">
              {clientsList.length === 0 ? (
                <div className="admin-no-clients-msg">
                  <FaUsersCog size={40} className="mb-3 opacity-50 text-white" />
                  <p className="fw-bold text-white mb-2">No clients found</p>
                  <p className="small text-muted mb-0">Please Log Out and Log In again to fetch your client list.</p>
                </div>
              ) : (
                <>
                  {!collapsed && (
                    <div className="admin-sidebar-search-box">
                      <FaSearch className="admin-sidebar-search-icon" />
                      <input
                        className="admin-sidebar-search-input"
                        placeholder="Search clients..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  )}
                  <ul className="admin-full-client-list">
                    {clientsList
                      .filter(c => !trimmedQuery || (c.center || c.org || c.userId || c.clientId || '').toLowerCase().includes(trimmedQuery))
                      .map(client => (
                        <li
                          key={client.clientId}
                          className="admin-fci-item"
                          onClick={() => { setSelectedClient(client); setSearchQuery(''); history.push('/'); if (!isDesktop && onClose) onClose(); }}
                        >
                          <div className="admin-fci-name">{client.center || client.org || client.userId}</div>
                          <div className="admin-fci-id">{client.clientId}</div>
                        </li>
                      ))
                    }
                    {clientsList.filter(c => !trimmedQuery || (c.org || c.userId || c.clientId || '').toLowerCase().includes(trimmedQuery)).length === 0 && (
                      <li className="admin-fci-empty">No matching clients</li>
                    )}
                  </ul>
                </>
              )}
            </div>

          ) : (
            /* ── CLIENT CONFIG TABS (admin + client selected, or regular client) ─── */
            <nav className="sidebar-nav">
              {trimmedQuery ? (
                filteredItems.length > 0 ? (
                  <div className="sidebar-group">
                    <span className="sidebar-group-label">Search results</span>
                    <ul className="sidebar-list">{filteredItems.map(item => renderNavItem(item, { locked: isItemLocked(item) }))}</ul>
                  </div>
                ) : (
                  <div className="sidebar-search-empty"><span>No matches for &quot;{searchQuery.trim()}&quot;</span></div>
                )
              ) : (
                NAV_GROUPS.map(group => (
                  <div key={group.label} className="sidebar-group">
                    <span className="sidebar-group-label">{group.label}</span>
                    <ul className="sidebar-list">{group.items.map(item => renderNavItem(item, { locked: isItemLocked(item) }))}</ul>
                  </div>
                ))
              )}
            </nav>
          )}
        </div>

        {/* ── Footer ──────────────────────────────────────────────────── */}
        <div className="sidebar-footer">
          {isDesktop && (
            <div className="toggle-btn-container">
              <button className="sidebar-toggle-btn" onClick={toggleCollapsed} title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
                <FaAngleDoubleLeft />
              </button>
            </div>
          )}
          <button type="button" className="sidebar-link sidebar-link--logout" onClick={() => setShowLogoutConfirm(true)} title={collapsed ? "Logout" : ""}>
            <span className="sidebar-link__icon"><i className="fas fa-sign-out-alt" /></span>
            <span className="sidebar-link__text">Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
